import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  Eye,
  Inbox,
  Mail,
  MessageSquareText,
  Phone,
  Save,
  ShieldCheck,
  Star,
  Trash2,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { FilterPanel, SortableHeader } from "../../components/data-controls";
import { LiaDateRangeField } from "../../components/date-picker";
import {
  ActionMenu,
  ActionMenuItem,
  Badge,
  Button,
  Card,
  Confirm,
  DataTableShell,
  Drawer,
  EmptyState,
  ErrorState,
  FormErrorSummary,
  Loading,
  Notice,
  PageHeader,
  Pagination,
  RatingStars,
  SearchBox,
  Select,
  SummaryGrid,
  Textarea,
  Input,
} from "../../components/ui";
import { adminApi } from "../../lib/api";
import { contactUpdateSchema, type ContactUpdateFormValues } from "../../lib/form-schemas";
import { dateTime, number } from "../../lib/format";
import { labelOf } from "../../lib/labels";
import type { AdminContactMessage, AdminReview, ContactStatus, ReviewStatus } from "../../types/api";

const reviewStatuses: ReviewStatus[] = ["Pending", "Approved", "Rejected"];
const contactStatuses: ContactStatus[] = ["New", "InProgress", "Closed"];
const toStartIso = (value: string) => value ? new Date(`${value}T00:00:00`).toISOString() : undefined;
const toEndIso = (value: string) => value ? new Date(`${value}T23:59:59.999`).toISOString() : undefined;

export function ReviewsPage() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<ReviewStatus | "">("");
  const [rating, setRating] = useState<number | "">("");
  const [productId, setProductId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selected, setSelected] = useState<AdminReview | null>(null);
  const [deleting, setDeleting] = useState<AdminReview | null>(null);

  const query = useQuery({
    queryKey: ["reviews", status, rating, productId, from, to, search, sort, page, pageSize],
    queryFn: () => adminApi.reviews.list({
      status,
      rating,
      productId,
      from: toStartIso(from),
      to: toEndIso(to),
      search,
      sort,
      page,
      pageSize,
    }),
  });
  const summary = useQuery({ queryKey: ["review-summary"], queryFn: adminApi.reviews.summary });
  const products = useQuery({ queryKey: ["review-products"], queryFn: () => adminApi.products.list({ page: 1, pageSize: 100, sort: "name-asc" }) });

  const moderate = useMutation({
    mutationFn: ({ id, status: nextStatus }: { id: string; status: ReviewStatus }) => adminApi.reviews.moderate(id, nextStatus),
    onSuccess: async () => {
      setSelected(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["reviews"] }),
        queryClient.invalidateQueries({ queryKey: ["review-summary"] }),
      ]);
    },
  });
  const remove = useMutation({
    mutationFn: adminApi.reviews.remove,
    onSuccess: async () => {
      setDeleting(null);
      setSelected(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["reviews"] }),
        queryClient.invalidateQueries({ queryKey: ["review-summary"] }),
      ]);
    },
  });

  const activeFilters = [search, status, rating, productId, from, to].filter(Boolean).length;
  const changeSort = (value: string) => { setSort(value); setPage(1); };

  return (
    <>
      <PageHeader title="مدیریت دیدگاه‌ها" description="دیدگاه‌های مشتریان را با امتیاز، وضعیت، محصول و بازه زمانی بررسی و مدیریت کنید." />

      <SummaryGrid compact items={[
        { label: "کل دیدگاه‌ها", value: number(summary.data?.total || 0), icon: <MessageSquareText />, tone: "neutral" },
        { label: "در انتظار تأیید", value: number(summary.data?.pending || 0), icon: <Eye />, tone: "gold" },
        { label: "تأییدشده", value: number(summary.data?.approved || 0), icon: <Check />, tone: "green" },
        { label: "میانگین امتیاز", value: `${number(summary.data?.averageRating || 0)} از ۵`, icon: <Star />, tone: "teal" },
      ]} />

      <FilterPanel
        search={<SearchBox value={search} onChange={(value) => { setSearch(value); setPage(1); }} placeholder="نام مشتری، محصول یا متن دیدگاه..." />}
        activeCount={activeFilters}
        totalLabel={`${number(query.data?.totalCount || 0)} دیدگاه`}
        onClear={() => { setSearch(""); setStatus(""); setRating(""); setProductId(""); setFrom(""); setTo(""); setSort("newest"); setPage(1); }}
      >
        <Select label="وضعیت دیدگاه" value={status} onChange={(event) => { setStatus(event.target.value as ReviewStatus | ""); setPage(1); }}>
          <option value="">همه وضعیت‌ها</option>
          {reviewStatuses.map((value) => <option key={value} value={value}>{labelOf(value)}</option>)}
        </Select>
        <Select label="امتیاز" value={rating} onChange={(event) => { setRating(event.target.value ? Number(event.target.value) : ""); setPage(1); }}>
          <option value="">همه امتیازها</option>
          {[5, 4, 3, 2, 1].map((value) => <option value={value} key={value}>{number(value)} ستاره</option>)}
        </Select>
        <Select label="محصول" value={productId} onChange={(event) => { setProductId(event.target.value); setPage(1); }}>
          <option value="">همه محصولات</option>
          {products.data?.items.map((item) => <option value={item.product.id} key={item.product.id}>{item.product.name}</option>)}
        </Select>
        <LiaDateRangeField
          className="filter-date-range"
          label="بازه ثبت دیدگاه"
          start={from}
          end={to}
          onChange={(range) => { setFrom(range.start); setTo(range.end); setPage(1); }}
        />
        <Select label="مرتب‌سازی" value={sort} onChange={(event) => changeSort(event.target.value)}>
          <option value="newest">جدیدترین</option>
          <option value="oldest">قدیمی‌ترین</option>
          <option value="rating-desc">بیشترین امتیاز</option>
          <option value="rating-asc">کمترین امتیاز</option>
          <option value="product-asc">نام محصول: الف تا ی</option>
          <option value="product-desc">نام محصول: ی تا الف</option>
        </Select>
      </FilterPanel>

      {query.isLoading ? <Loading /> : query.error ? <ErrorState error={query.error} retry={() => void query.refetch()} /> : !query.data?.items.length ? <EmptyState title="دیدگاهی پیدا نشد" /> : (
        <DataTableShell pagination={<Pagination page={query.data.page} totalPages={query.data.totalPages} totalCount={query.data.totalCount} pageSize={pageSize} onChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} />}>
          <table className="product-table management-table">
            <thead>
              <tr>
                <th>مشتری</th>
                <SortableHeader label="محصول" value={sort} ascValue="product-asc" descValue="product-desc" onChange={changeSort} />
                <SortableHeader label="امتیاز" value={sort} ascValue="rating-asc" descValue="rating-desc" defaultDirection="desc" onChange={changeSort} />
                <th>دیدگاه</th>
                <th>وضعیت</th>
                <SortableHeader label="تاریخ" value={sort} ascValue="oldest" descValue="newest" defaultDirection="desc" onChange={changeSort} />
                <th>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((review) => (
                <tr key={review.id}>
                  <td data-label="مشتری"><div className="inbox-sender"><span className="inbox-sender__avatar">{review.displayName.slice(0, 1)}</span><div className="cell-stack"><strong>{review.displayName}</strong>{review.isVerifiedPurchase ? <small className="success-text">خرید تأییدشده</small> : <small>بدون تأیید خرید</small>}</div></div></td>
                  <td data-label="محصول">{review.productName}</td>
                  <td data-label="امتیاز"><RatingStars value={review.rating} /></td>
                  <td data-label="دیدگاه"><div className="cell-stack"><strong>{review.title || "بدون عنوان"}</strong><small className="line-clamp">{review.comment}</small></div></td>
                  <td data-label="وضعیت"><Badge value={labelOf(review.status)} /></td>
                  <td data-label="تاریخ">{dateTime(review.createdAt)}</td>
                  <td data-label="عملیات">
                    <ActionMenu>
                      <ActionMenuItem onClick={() => setSelected(review)}><Eye size={15} /> مشاهده کامل</ActionMenuItem>
                      <ActionMenuItem disabled={review.status === "Approved" || moderate.isPending} onClick={() => moderate.mutate({ id: review.id, status: "Approved" })}><Check size={15} /> تأیید</ActionMenuItem>
                      <ActionMenuItem disabled={review.status === "Rejected" || moderate.isPending} onClick={() => moderate.mutate({ id: review.id, status: "Rejected" })}><X size={15} /> رد</ActionMenuItem>
                      <ActionMenuItem danger onClick={() => setDeleting(review)}><Trash2 size={15} /> حذف</ActionMenuItem>
                    </ActionMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTableShell>
      )}

      <Drawer open={Boolean(selected)} title="جزئیات دیدگاه" description={selected ? `${selected.productName} · ${dateTime(selected.createdAt)}` : undefined} onClose={() => setSelected(null)}>
        {selected ? (
          <div className="stack">
            <Card title="اطلاعات مشتری">
              <div className="cell-stack"><strong>{selected.displayName}</strong><span>{selected.isVerifiedPurchase ? "خرید این محصول تأیید شده است." : "خرید این محصول برای این دیدگاه تأیید نشده است."}</span></div>
            </Card>
            <Card title="امتیاز و دیدگاه">
              <div className="stack">
                <RatingStars value={selected.rating} size={20} />
                {selected.title ? <strong>{selected.title}</strong> : null}
                <p className="message-body">{selected.comment}</p>
              </div>
            </Card>
            <div className="form-actions">
              <Button variant="secondary" disabled={selected.status === "Rejected"} busy={moderate.isPending} onClick={() => moderate.mutate({ id: selected.id, status: "Rejected" })}><X size={16} /> رد دیدگاه</Button>
              <Button disabled={selected.status === "Approved"} busy={moderate.isPending} onClick={() => moderate.mutate({ id: selected.id, status: "Approved" })}><Check size={16} /> تأیید دیدگاه</Button>
            </div>
          </div>
        ) : null}
      </Drawer>

      <Confirm open={Boolean(deleting)} title="حذف دیدگاه" description={`دیدگاه «${deleting?.displayName || ""}» حذف شود؟ پس از حذف، میانگین امتیاز محصول دوباره محاسبه می‌شود.`} confirmLabel="حذف دیدگاه" busy={remove.isPending} onClose={() => setDeleting(null)} onConfirm={() => deleting && remove.mutate(deleting.id)} />
    </>
  );
}

export function ContactsPage() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<ContactStatus | "">("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selected, setSelected] = useState<AdminContactMessage | null>(null);
  const [deleting, setDeleting] = useState<AdminContactMessage | null>(null);
  const form = useForm<ContactUpdateFormValues>({
    resolver: zodResolver(contactUpdateSchema),
    mode: "onBlur",
    defaultValues: { status: "InProgress", adminNote: "" },
  });

  const query = useQuery({
    queryKey: ["contacts", status, from, to, search, sort, page, pageSize],
    queryFn: () => adminApi.contacts.list({ status, from: toStartIso(from), to: toEndIso(to), search, sort, page, pageSize }),
  });
  const summary = useQuery({ queryKey: ["contact-summary"], queryFn: adminApi.contacts.summary });

  const mutation = useMutation({
    mutationFn: (values: ContactUpdateFormValues) => {
      if (!selected) throw new Error("پیامی برای ویرایش انتخاب نشده است.");
      return adminApi.contacts.update(selected.id, values.status, values.adminNote?.trim() || undefined);
    },
    onSuccess: async () => {
      setSelected(null);
      form.reset({ status: "InProgress", adminNote: "" });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["contacts"] }),
        queryClient.invalidateQueries({ queryKey: ["contact-summary"] }),
      ]);
    },
  });
  const quickStatus = useMutation({
    mutationFn: ({ id, status: nextStatus, note }: { id: string; status: ContactStatus; note?: string }) => adminApi.contacts.update(id, nextStatus, note),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["contacts"] }),
        queryClient.invalidateQueries({ queryKey: ["contact-summary"] }),
      ]);
    },
  });
  const remove = useMutation({
    mutationFn: adminApi.contacts.remove,
    onSuccess: async () => {
      setDeleting(null);
      setSelected(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["contacts"] }),
        queryClient.invalidateQueries({ queryKey: ["contact-summary"] }),
      ]);
    },
  });

  const open = (message: AdminContactMessage) => {
    setSelected(message);
    form.reset({ status: message.status, adminNote: message.adminNote ?? "" });
  };
  const close = () => {
    if (mutation.isPending) return;
    setSelected(null);
    form.reset({ status: "InProgress", adminNote: "" });
  };
  const activeFilters = [search, status, from, to].filter(Boolean).length;
  const changeSort = (value: string) => { setSort(value); setPage(1); };

  return (
    <>
      <PageHeader title="پیام‌های تماس" description="پیام‌های مشتریان را مثل یک Inbox مدیریتی بررسی، پاسخ‌گیری و وضعیت‌بندی کنید." />

      <SummaryGrid compact items={[
        { label: "کل پیام‌ها", value: number(summary.data?.total || 0), icon: <Inbox />, tone: "neutral" },
        { label: "خوانده‌نشده / جدید", value: number(summary.data?.new || 0), icon: <Mail />, tone: "gold" },
        { label: "در حال پیگیری", value: number(summary.data?.inProgress || 0), icon: <MessageSquareText />, tone: "teal" },
        { label: "بسته‌شده", value: number(summary.data?.closed || 0), icon: <Check />, tone: "green" },
      ]} />

      <FilterPanel
        search={<SearchBox value={search} onChange={(value) => { setSearch(value); setPage(1); }} placeholder="نام، تلفن، ایمیل، موضوع یا متن پیام..." />}
        activeCount={activeFilters}
        totalLabel={`${number(query.data?.totalCount || 0)} پیام`}
        onClear={() => { setSearch(""); setStatus(""); setFrom(""); setTo(""); setSort("newest"); setPage(1); }}
      >
        <Select label="وضعیت پیگیری" value={status} onChange={(event) => { setStatus(event.target.value as ContactStatus | ""); setPage(1); }}>
          <option value="">همه وضعیت‌ها</option>
          {contactStatuses.map((value) => <option value={value} key={value}>{labelOf(value)}</option>)}
        </Select>
        <LiaDateRangeField
          className="filter-date-range"
          label="بازه دریافت پیام"
          start={from}
          end={to}
          onChange={(range) => { setFrom(range.start); setTo(range.end); setPage(1); }}
        />
        <Select label="مرتب‌سازی" value={sort} onChange={(event) => changeSort(event.target.value)}>
          <option value="newest">جدیدترین</option>
          <option value="oldest">قدیمی‌ترین</option>
          <option value="name-asc">نام فرستنده: الف تا ی</option>
          <option value="name-desc">نام فرستنده: ی تا الف</option>
          <option value="subject-asc">موضوع: الف تا ی</option>
          <option value="subject-desc">موضوع: ی تا الف</option>
          <option value="status">وضعیت پیگیری</option>
        </Select>
      </FilterPanel>

      {query.isLoading ? <Loading /> : query.error ? <ErrorState error={query.error} retry={() => void query.refetch()} /> : !query.data?.items.length ? <EmptyState title="پیامی پیدا نشد" /> : (
        <DataTableShell pagination={<Pagination page={query.data.page} totalPages={query.data.totalPages} totalCount={query.data.totalCount} pageSize={pageSize} onChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} />}>
          <table className="product-table management-table">
            <thead>
              <tr>
                <SortableHeader label="فرستنده" value={sort} ascValue="name-asc" descValue="name-desc" onChange={changeSort} />
                <SortableHeader label="موضوع" value={sort} ascValue="subject-asc" descValue="subject-desc" onChange={changeSort} />
                <th>وضعیت</th>
                <SortableHeader label="تاریخ" value={sort} ascValue="oldest" descValue="newest" defaultDirection="desc" onChange={changeSort} />
                <th>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((message) => (
                <tr className={message.status === "New" ? "unread-row" : ""} key={message.id}>
                  <td data-label="فرستنده"><div className="inbox-sender"><span className="inbox-sender__avatar">{message.fullName.slice(0, 1)}</span><div className="cell-stack"><strong>{message.fullName}</strong><small dir="ltr">{message.phoneNumber}</small>{message.email ? <small dir="ltr">{message.email}</small> : null}</div></div></td>
                  <td data-label="موضوع"><div className="cell-stack"><strong>{message.subject}</strong><small className="line-clamp">{message.message}</small></div></td>
                  <td data-label="وضعیت"><Badge value={labelOf(message.status)} /></td>
                  <td data-label="تاریخ">{dateTime(message.createdAt)}</td>
                  <td data-label="عملیات">
                    <ActionMenu>
                      <ActionMenuItem onClick={() => open(message)}><Eye size={15} /> مشاهده و پیگیری</ActionMenuItem>
                      {message.status === "New" ? <ActionMenuItem onClick={() => quickStatus.mutate({ id: message.id, status: "InProgress", note: message.adminNote || undefined })}><Check size={15} /> علامت‌گذاری خوانده‌شده</ActionMenuItem> : <ActionMenuItem onClick={() => quickStatus.mutate({ id: message.id, status: "New", note: message.adminNote || undefined })}><Mail size={15} /> بازگرداندن به جدید</ActionMenuItem>}
                      <ActionMenuItem danger onClick={() => setDeleting(message)}><Trash2 size={15} /> حذف پیام</ActionMenuItem>
                    </ActionMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTableShell>
      )}

      <Drawer open={Boolean(selected)} title="جزئیات پیام تماس" description={selected ? dateTime(selected.createdAt) : undefined} onClose={close} wide>
        {selected ? (
          <div className="stack">
            <Card title={selected.subject} description={`ارسال‌کننده: ${selected.fullName}`}>
              <div className="contact-actions">
                <a href={`tel:${selected.phoneNumber}`}><Phone size={15} /> تماس با {selected.phoneNumber}</a>
                {selected.email ? <a href={`mailto:${selected.email}`}><Mail size={15} /> ارسال ایمیل</a> : null}
              </div>
              <p className="message-body mt-4">{selected.message}</p>
              {selected.attachmentUrl ? <a className="text-link mt-3 inline-flex" href={selected.attachmentUrl} target="_blank" rel="noreferrer">مشاهده فایل پیوست</a> : null}
            </Card>

            <Card title="پیگیری داخلی" description="این اطلاعات فقط در پنل مدیریت قابل مشاهده است.">
              <form className="stack" onSubmit={form.handleSubmit((values) => mutation.mutate(values))} noValidate>
                <FormErrorSummary errors={[form.formState.errors.status?.message, form.formState.errors.adminNote?.message]} />
                <Select label="وضعیت" error={form.formState.errors.status?.message} {...form.register("status")}>
                  {contactStatuses.map((value) => <option value={value} key={value}>{labelOf(value)}</option>)}
                </Select>
                <Textarea label="یادداشت داخلی مدیر" rows={6} hint="نتیجه تماس، پاسخ مشتری یا اقدام بعدی را ثبت کنید." error={form.formState.errors.adminNote?.message} {...form.register("adminNote")} />
                {mutation.error ? <small className="field__error">{mutation.error.message}</small> : null}
                <div className="form-actions"><Button variant="ghost" type="button" onClick={close}>انصراف</Button><Button type="submit" busy={mutation.isPending} disabled={!form.formState.isDirty}><Save size={16} /> ثبت پیگیری</Button></div>
              </form>
            </Card>
          </div>
        ) : null}
      </Drawer>

      <Confirm open={Boolean(deleting)} title="حذف پیام تماس" description={`پیام «${deleting?.subject || ""}» از ${deleting?.fullName || ""} حذف شود؟`} confirmLabel="حذف پیام" busy={remove.isPending} onClose={() => setDeleting(null)} onConfirm={() => deleting && remove.mutate(deleting.id)} />
    </>
  );
}

export function SettingsPage() {
  const integrations = [
    { key: "torob", name: "ترب", category: "مارکت‌پلیس و مقایسه قیمت", description: "آماده برای اتصال آینده به سرویس‌های ترب جهت همگام‌سازی کاتالوگ، قیمت و موجودی بر اساس قرارداد API نهایی.", badge: "اتصال در آینده" },
    { key: "digikala", name: "دیجی‌کالا", category: "Marketplace / Seller API", description: "محل تنظیم اتصال آینده به وب‌سرویس فروشندگان دیجی‌کالا؛ بدون ذخیره Secret یا داده ساختگی تا زمان دریافت دسترسی رسمی.", badge: "اتصال در آینده" },
    { key: "smsir", name: "SMS.ir", category: "پیامک تراکنشی و OTP", description: "برای ارسال OTP ورود دومرحله‌ای، پیامک‌های سفارش و اعلان‌های تراکنشی. Provider واقعی در محیط Production باید با کلیدهای امن پیکربندی شود.", badge: "نیازمند پیکربندی" },
  ];
  return <>
    <PageHeader title="تنظیمات پنل" description="مرکز اتصال سرویس‌های خارجی موردنیاز فروشگاه؛ فقط Integrationهایی که قرار است در ادامه پروژه استفاده شوند نمایش داده می‌شوند." />
    <div className="integration-grid">{integrations.map((item) => <Card className="integration-card" key={item.key}>
      <div className="integration-card__head"><span className={`integration-logo integration-logo--${item.key}`}>{item.name.slice(0, 2)}</span><div><h2>{item.name}</h2><p>{item.category}</p></div><Badge value={item.badge} tone="neutral" /></div>
      <p className="integration-card__description">{item.description}</p>
      <div className="integration-card__footer"><span>وضعیت</span><strong>غیرفعال — منتظر اطلاعات وب‌سرویس</strong></div>
    </Card>)}</div>
    <Notice>در این نسخه هیچ API Key یا Secret آزمایشی داخل Frontend ذخیره نمی‌شود. هنگام اتصال واقعی ترب، دیجی‌کالا و SMS.ir، کلیدها باید فقط در Backend و Secret/Environment امن نگهداری شوند.</Notice>
  </>;
}

